import { remove } from 'lodash';
import {
  duplicateOrganisationUnitService,
  fetchOrganisationOverviewService,
  getOrganisationUnitPersonService,
  getOrganisationUnitPersonsService,
  getOrganisationUnitService,
  getOrganisationUnitsService,
  toggleOrganisationUnitSelectionService,
  updateCompletionStateService,
  updateOrganisationUnitStateService,
  updateOrganisationUnitRolesService,
} from '../services/organisationService';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// async thunk
export const requestOrganisationOverview = createAsyncThunk(
  'organisation/requestOrganisationOverview',
  async (resetState) => {
    const response = await fetchOrganisationOverviewService();
    return response.data;
  }
);

export const getOrganisationUnitPerson = createAsyncThunk(
  'organisation/getOrganisationUnitPerson',
  async ({ personId, unitId }) => {
    const response = await getOrganisationUnitPersonService(personId);
    return response.data;
  }
);

export const getOrganisationUnitMultiplePersons = createAsyncThunk(
  'organisation/getOrganisationUnitMultiplePersons',
  async (personIds) => {
    const response = await getOrganisationUnitPersonsService(personIds);
    return response.data;
  }
);

export const getOrganisationUnit = createAsyncThunk(
  'organisation/getOrganisationUnit',
  async (unitId) => {
    const response = await getOrganisationUnitService(unitId);
    return response.data;
  }
);

export const getOrganisationsUnits = createAsyncThunk(
  'organisation/getOrganisationsUnits',
  async ({ unitIds }) => {
    const response = await getOrganisationUnitsService({
      organisationUnits: unitIds,
    });
    return response.data;
  }
);

export const updateCompletionState = createAsyncThunk(
  'organisation/updateCompletionState',
  async (completionStateValue) => {
    const response = await updateCompletionStateService(completionStateValue);
    return completionStateValue;
  }
);

export const duplicateOrganisationUnit = createAsyncThunk(
  'organisation/duplicateOrganisationUnit',
  async ({ unitId, name, include }, { dispatch }) => {
    try {
      const response = await duplicateOrganisationUnitService(
        unitId,
        name,
        include
      );
      await toggleOrganisationUnitSelectionService({
        organisationUnitId: response.data.newOrganisationUnitId,
      });
      await dispatch(requestOrganisationOverview());
      return response.data;
    } catch (error) {
      console.error('duplicateOrganisationUnit error:', error);
      throw error;
    }
  }
);

export const updateOrgnisationUnitState = createAsyncThunk(
  'organisation/updateOrgnisationUnitState',
  async ({ unitId, state }) => {
    const response = await updateOrganisationUnitStateService(unitId, state);
    return { unitId, state, ...response.data };
  }
);

export const toggleOrganisationUnitSelection = createAsyncThunk(
  'organisation/toggleOrganisationUnitSelection',
  async ({ unitId }) => {
    const response = await toggleOrganisationUnitSelectionService({
      organisationUnitId: unitId,
    });
    return response.data;
  }
);

export const updateOrganisationUnitRoles = createAsyncThunk(
  'organisation/updateOrganisationUnitRoles',
  async ({ unitId, roles }) => {
    const response = await updateOrganisationUnitRolesService(unitId, {
      roles,
    });
    return response.data;
  }
);

// slice
const organisationSlice = createSlice({
  name: 'organisation',
  initialState: {
    categories: {},
    categoriesSortOrder: [],
    knowledgeAreas: {},
    organisationUnitRootNodes: [],
    organisationUnits: {},
    organisationUnitsExpanded: [],
    organisationUnitSortOrder: [],
    persons: {},
    personsSortOrder: [],
    roles: {},
    selectedOrganisationUnits: [],
    timeline: {},
    isFetchingOrganisationOverview: false,
    isLoading: false,
    error: null,
  },
  reducers: {
    removeRole: (state, action) => {
      const { roleId } = action.payload;
      state.roles = remove(state.roles, (role) => role.id !== roleId);
      state.units.forEach((unit) => {
        unit.roles = remove(unit.roles, (role) => role.id !== roleId);
      });
    },
    createdOrganisationPersonDataReceived: (state, action) => {
      state.persons = {
        ...state.persons,
        ...action.payload.persons,
      };
    },
    removePersonRole: (state, action) => {
      const { personId, roleId } = action.payload;
      if (state.persons[personId] && state.persons[personId].roles) {
        const { [roleId]: removedRole, ...remainingRoles } =
          state.persons[personId].roles;
        state.persons[personId] = {
          ...state.persons[personId],
          roles: remainingRoles,
        };
      }
    },
    updatedOrganisationUnitDataReceived: (state, action) => {
      state.knowledgeAreas = addElementsToState(
        state.knowledgeAreas,
        action.payload.knowledgeAreas
      );
      state.organisationUnits = {
        ...state.organisationUnits,
        ...action.payload.organisationUnits,
      };
      state.roles = {
        ...state.roles,
        ...action.payload.roles,
      };
      state.persons = addPersonsToState(state.persons, action.payload.persons);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestOrganisationOverview.pending, (state) => {
        state.isFetchingOrganisationOverview = true;
        state.error = null;
      })
      .addCase(requestOrganisationOverview.fulfilled, (state, action) => {
        const resetState = action.meta.arg;
        if (resetState) {
          state.organisationUnitRootNodes =
            action.payload.organisationUnitRootNodes;
          state.organisationUnitSortOrder =
            action.payload.organisationUnitSortOrder;
          state.organisationUnits = action.payload.organisationUnits;
          state.organisationUnitsExpanded =
            action.payload.organisationUnitsExpanded;
          state.roles = action.payload.roles;
          state.persons = action.payload.persons;
          state.selectedOrganisationUnits =
            action.payload.selectedOrganisationUnits;
          state.showCompletion = action.payload.showCompletion;
          state.completionState = action.payload.completionState;
          state.isReadOnly = action.payload.isReadOnly;
          state.timeline = action.payload.timeline || {};
          state.hasOverviewLoaded = true;
        } else {
          state.isFetchingOrganisationOverview = false;
          state.isLoading = false;
          state.error = null;
          state.organisationUnitRootNodes =
            action.payload.organisationUnitRootNodes || [];
          state.organisationUnits = addElementsToState(
            state.organisationUnits,
            action.payload.organisationUnits
          );
          state.organisationUnitSortOrder =
            action.payload.organisationUnitSortOrder || [];
          state.categories = addElementsToState(
            state.categories,
            action.payload.categories
          );
          state.categoriesSortOrder = action.payload.categoriesSortOrder || [];
          state.knowledgeAreas = addElementsToState(
            state.knowledgeAreas,
            action.payload.knowledgeAreas
          );
          state.roles = addElementsToState(state.roles, action.payload.roles);
          state.persons = addPersonsToState(
            state.persons,
            action.payload.persons
          );
          state.personsSortOrder = action.payload.personsSortOrder || [];
          state.selectedOrganisationUnits =
            action.payload.selectedOrganisationUnits || [];
          state.completionState = action.payload.completionState || {};
          state.organisationUnitsExpanded =
            action.payload.organisationUnitsExpanded || [];
          state.timeline = action.payload.timeline || {};
        }
      })
      .addCase(requestOrganisationOverview.rejected, (state, action) => {
        state.isFetchingOrganisationOverview = false;
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(getOrganisationUnitPerson.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOrganisationUnitPerson.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        const personData = action.payload.person;
        if (personData && personData.id) {
          const transformedPayload = {
            [personData.id]: personData,
          };
          const updatedPersons = addPersonsToState(
            state.persons,
            transformedPayload
          );
          state.persons = updatedPersons;
        }
        if (action.payload.organisationUnits) {
          state.organisationUnits = {
            ...state.organisationUnits,
            ...action.payload.organisationUnits,
          };
        }
      })
      .addCase(getOrganisationUnitPerson.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(getOrganisationUnitMultiplePersons.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        getOrganisationUnitMultiplePersons.fulfilled,
        (state, action) => {
          state.isLoading = false;
          state.error = null;
          state.persons = addPersonsToState(state.persons, action.payload);
        }
      )
      .addCase(getOrganisationUnitMultiplePersons.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(getOrganisationUnit.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOrganisationUnit.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.knowledgeAreas = addElementsToState(
          state.knowledgeAreas,
          action.payload.knowledgeAreas
        );
        state.organisationUnits = addElementsToState(
          state.organisationUnits,
          action.payload.organisationUnits
        );
        state.roles = addElementsToState(state.roles, action.payload.roles);
        state.persons = addPersonsToState(
          state.persons,
          action.payload.persons
        );
      })
      .addCase(getOrganisationUnit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(getOrganisationsUnits.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOrganisationsUnits.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.knowledgeAreas = addElementsToState(
          state.knowledgeAreas,
          action.payload.knowledgeAreas
        );
        state.organisationUnits = addElementsToState(
          state.organisationUnits,
          action.payload.organisationUnits
        );
        state.persons = addPersonsToState(
          state.persons,
          action.payload.persons
        );
        state.roles = addElementsToState(state.roles, action.payload.roles);
      })
      .addCase(getOrganisationsUnits.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(updateCompletionState.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCompletionState.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.completionState = action.payload;
      })
      .addCase(updateCompletionState.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(duplicateOrganisationUnit.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(duplicateOrganisationUnit.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(duplicateOrganisationUnit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(updateOrgnisationUnitState.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOrgnisationUnitState.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        const { unitId, state: newState } = action.payload;
        if (state.organisationUnits[unitId]) {
          state.organisationUnits[unitId] = {
            ...state.organisationUnits[unitId],
            state: newState,
          };
        }
      })
      .addCase(updateOrgnisationUnitState.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      .addCase(toggleOrganisationUnitSelection.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleOrganisationUnitSelection.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        const { organisationUnitId, data } = action.payload;
        if (data.selected) {
          state.selectedOrganisationUnits.push(organisationUnitId);
        } else {
          state.selectedOrganisationUnits =
            state.selectedOrganisationUnits.filter(
              (id) => id !== organisationUnitId
            );
        }
      })
      .addCase(toggleOrganisationUnitSelection.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });
  },
});

export const {
  removeRole,
  createdOrganisationPersonDataReceived,
  updatedOrganisationUnitDataReceived,
  removePersonRole,
} = organisationSlice.actions;
export default organisationSlice.reducer;

// helpers
const addElementsToState = (stateElements, newElements) => {
  if (!newElements) return stateElements;

  const elementObj = Object.keys(newElements).reduce((obj, key) => {
    if (stateElements[key]) {
      obj[key] = {
        ...stateElements[key],
        ...newElements[key],
      };
    } else {
      obj[key] = newElements[key];
    }
    return obj;
  }, {});
  return {
    ...stateElements,
    ...elementObj,
  };
};

const addPersonsToState = (stateElements, newElements) => {
  if (!newElements) return stateElements;

  const elementObj = Object.keys(newElements).reduce((obj, key) => {
    if (stateElements[key]) {
      obj[key] = {
        ...stateElements[key],
        ...newElements[key],
        roles: {
          ...stateElements[key].roles,
          ...newElements[key].roles,
        },
      };
    } else {
      obj[key] = newElements[key];
    }
    return obj;
  }, {});
  return {
    ...stateElements,
    ...elementObj,
  };
};
